import { useEffect, useState } from 'react';
import { OperatingTypes } from '~/app/data/statuses/operatingData';
import {
    getPeriodicCheckQueueVersion,
    subscribePeriodicCheckQueue,
} from '~/data/sync/periodicCheckQueue';
import {
    isOperatingDataAwaitingSync,
    listQueuedOperatingData,
} from '~/data/sync/operatingDataQueue';
import { useStoreState } from '~/store';
import { Machine } from '~/types';

export function usePendingOperatingData(machineId?: Machine['id']): {
    operatingTime: boolean
    operatingCount: boolean
    loaded: boolean
} {
    const userId = useStoreState(state => state.user.data?.id);
    const operatingTimePendingSync = useStoreState(state =>
        String(state.machine.data.id) === String(machineId ?? '') && !!state.machine.data.operatingTimePendingSync
    );
    const operatingCountPendingSync = useStoreState(state =>
        String(state.machine.data.id) === String(machineId ?? '') && !!state.machine.data.operatingCountPendingSync
    );
    const [queueVersion, setQueueVersion] = useState(getPeriodicCheckQueueVersion);
    const [queuedTime, setQueuedTime] = useState(false);
    const [queuedCount, setQueuedCount] = useState(false);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => subscribePeriodicCheckQueue(setQueueVersion), []);

    useEffect(() => {
        let cancelled = false;
        if (!userId || !machineId || !window.sqlitePlugin) {
            setQueuedTime(false);
            setQueuedCount(false);
            setLoaded(true);
            return;
        }
        listQueuedOperatingData(Number(userId)).then(rows => {
            if (cancelled) {
                return;
            }
            const mine = rows.filter(row => String(row.machineId) === String(machineId));
            setQueuedTime(mine.some(row => Number(row.type) === OperatingTypes.OPERATING_TIME));
            setQueuedCount(mine.some(row => Number(row.type) === OperatingTypes.OPERATING_COUNT));
            setLoaded(true);
        }).catch(() => {
            if (!cancelled) {
                setQueuedTime(false);
                setQueuedCount(false);
                setLoaded(true);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [userId, machineId, queueVersion, operatingTimePendingSync, operatingCountPendingSync]);

    return {
        operatingTime: isOperatingDataAwaitingSync(queuedTime, loaded, operatingTimePendingSync),
        operatingCount: isOperatingDataAwaitingSync(queuedCount, loaded, operatingCountPendingSync),
        loaded,
    };
}
